import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FileText, Plus, Clock, CheckCircle, AlertCircle, Send, ClipboardList,
  BookOpen, Users, Award, ChevronRight, Pencil, Trash2, Eye, Star,
  Calendar, GraduationCap, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  devoir: { label: 'Devoir', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: FileText },
  tp: { label: 'TP', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: ClipboardList },
  interrogation: { label: 'Interrogation', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: Pencil },
  examen: { label: 'Examen', color: 'bg-red-50 text-red-700 border-red-200', icon: Award },
};

export const AssignmentsPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [viewMode, setViewMode] = useState(null); // 'detail', 'submissions', 'submit'

  const isStudent = user?.role === 'student';
  const isTeacher = ['teacher', 'visiting_teacher'].includes(user?.role);
  const isAdmin = ['super_admin', 'co_admin', 'rector', 'dean', 'department_head', 'sg_academic'].includes(user?.role);

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/assignments`);
      setAssignments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    if (!isTeacher && !isAdmin) return;
    try {
      // Use classroom endpoint to get courses for this teacher
      const res = await axios.get(`${API_URL}/api/online-courses/my-classrooms`);
      const classrooms = Array.isArray(res.data) ? res.data : [];
      const allCourses = classrooms.flatMap(cr => cr.courses || []);
      setCourses(allCourses);
    } catch (err) {
      console.error(err);
    }
  }, [isTeacher, isAdmin]);

  useEffect(() => { fetchAssignments(); fetchCourses(); }, [fetchAssignments, fetchCourses]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce devoir ?')) return;
    try {
      await axios.delete(`${API_URL}/api/assignments/${id}`);
      toast.success('Devoir supprime');
      fetchAssignments();
      setSelectedAssignment(null);
      setViewMode(null);
    } catch { toast.error('Erreur'); }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-slide-in" data-testid="assignments-page">
        <div><h1 className="heading-2">Devoirs & TP</h1><p className="text-gray-500 mt-1">Gestion des travaux academiques</p></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3].map(i => <Card key={i} className="animate-pulse"><CardContent className="p-6"><div className="h-5 w-40 bg-gray-200 rounded mb-3" /><div className="h-4 w-24 bg-gray-200 rounded" /></CardContent></Card>)}
        </div>
      </div>
    );
  }

  // Detail/Submissions/Submit views
  if (selectedAssignment && viewMode) {
    return (
      <div className="space-y-4 animate-slide-in" data-testid="assignment-detail-view">
        <Button variant="ghost" onClick={() => { setSelectedAssignment(null); setViewMode(null); }} className="gap-2 text-gray-500 -ml-2" data-testid="back-to-assignments">
          <ChevronRight className="w-4 h-4 rotate-180" /> Retour aux devoirs
        </Button>
        {viewMode === 'submit' && <StudentSubmitView assignment={selectedAssignment} onDone={() => { setSelectedAssignment(null); setViewMode(null); fetchAssignments(); }} />}
        {viewMode === 'submissions' && <TeacherSubmissionsView assignment={selectedAssignment} />}
        {viewMode === 'detail' && <AssignmentDetailView assignment={selectedAssignment} isStudent={isStudent} isTeacher={isTeacher || isAdmin}
          onSubmit={() => setViewMode('submit')} onViewSubmissions={() => setViewMode('submissions')} onDelete={() => handleDelete(selectedAssignment.id)} />}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="assignments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2">Devoirs & TP</h1>
          <p className="text-gray-500 mt-1">{isStudent ? 'Vos travaux academiques' : 'Gestion des travaux academiques'}</p>
        </div>
        {(isTeacher || isAdmin) && (
          <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="create-assignment-button">
            <Plus className="w-4 h-4" /> Nouveau Devoir
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={FileText} label="Total" value={assignments.length} color="blue" />
        <StatCard icon={Clock} label={isStudent ? "A soumettre" : "En attente"} value={assignments.filter(a => isStudent ? !a.my_submission : true).length} color="orange" />
        {isStudent && <StatCard icon={CheckCircle} label="Soumis" value={assignments.filter(a => a.my_submission).length} color="green" />}
        {isStudent && <StatCard icon={Star} label="Notes" value={assignments.filter(a => a.my_submission?.status === 'graded').length} color="purple" />}
        {!isStudent && <StatCard icon={Users} label="Soumissions" value={assignments.reduce((s, a) => s + (a.submission_count || 0), 0)} color="green" />}
        {!isStudent && <StatCard icon={Award} label="Corriges" value={assignments.reduce((s, a) => s + (a.graded_count || 0), 0)} color="purple" />}
      </div>

      {/* Assignments List */}
      {assignments.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{isStudent ? 'Aucun devoir pour le moment' : 'Aucun devoir cree'}</h3>
          <p className="text-gray-500">{isStudent ? 'Vos enseignants n\'ont pas encore publie de devoirs.' : 'Creez votre premier devoir pour commencer.'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {assignments.map(a => (
            <AssignmentCard key={a.id} assignment={a} isStudent={isStudent} isTeacher={isTeacher || isAdmin}
              onView={() => { setSelectedAssignment(a); setViewMode('detail'); }}
              onSubmit={() => { setSelectedAssignment(a); setViewMode('submit'); }}
              onViewSubmissions={() => { setSelectedAssignment(a); setViewMode('submissions'); }}
            />
          ))}
        </div>
      )}

      {showCreate && <CreateAssignmentDialog courses={courses} onClose={() => setShowCreate(false)} onSuccess={() => { setShowCreate(false); fetchAssignments(); }} />}
    </div>
  );
};

// ============ STAT CARD ============
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = { blue: 'bg-blue-50 text-blue-600', orange: 'bg-orange-50 text-orange-600', green: 'bg-green-50 text-green-600', purple: 'bg-purple-50 text-purple-600' };
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}><Icon className="w-5 h-5" /></div>
        <div><p className="text-2xl font-bold text-gray-900">{value}</p><p className="text-xs text-gray-500">{label}</p></div>
      </CardContent>
    </Card>
  );
};

// ============ ASSIGNMENT CARD ============
const AssignmentCard = ({ assignment: a, isStudent, isTeacher, onView, onSubmit, onViewSubmissions }) => {
  const typeInfo = TYPE_CONFIG[a.assignment_type] || TYPE_CONFIG.devoir;
  const Icon = typeInfo.icon;
  const hasSubmitted = !!a.my_submission;
  const isGraded = a.my_submission?.status === 'graded';
  const isOverdue = a.due_date && new Date(a.due_date) < new Date() && !hasSubmitted;

  return (
    <Card className="card-hover group" data-testid={`assignment-card-${a.id}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border ${typeInfo.color}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-semibold text-gray-900 cursor-pointer hover:text-[var(--tenant-primary)]" onClick={onView}>{a.title}</h3>
              <Badge className={`text-xs ${typeInfo.color}`}>{typeInfo.label}</Badge>
              {isStudent && isGraded && <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">{a.my_submission.score}/{a.max_points}</Badge>}
              {isStudent && hasSubmitted && !isGraded && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">Soumis</Badge>}
              {isStudent && isOverdue && <Badge className="bg-red-50 text-red-700 border-red-200 text-xs">En retard</Badge>}
            </div>
            <p className="text-sm text-gray-500 line-clamp-1 mb-2">{a.description}</p>
            <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
              <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" />{a.teacher_name}</span>
              <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5" />{a.max_points} pts</span>
              {a.due_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(a.due_date).toLocaleDateString('fr-FR')}</span>}
              {!isStudent && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{a.submission_count || 0} soumission(s)</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={onView} className="gap-1.5 text-xs" data-testid={`view-assignment-${a.id}`}>
              <Eye className="w-3.5 h-3.5" /> Voir
            </Button>
            {isStudent && !hasSubmitted && (
              <Button size="sm" onClick={onSubmit} className="gap-1.5 text-xs" data-testid={`submit-assignment-${a.id}`}>
                <Send className="w-3.5 h-3.5" /> Soumettre
              </Button>
            )}
            {isTeacher && (
              <Button variant="outline" size="sm" onClick={onViewSubmissions} className="gap-1.5 text-xs" data-testid={`view-submissions-${a.id}`}>
                <ClipboardList className="w-3.5 h-3.5" /> Corriger
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ ASSIGNMENT DETAIL VIEW ============
const AssignmentDetailView = ({ assignment: a, isStudent, isTeacher, onSubmit, onViewSubmissions, onDelete }) => {
  const [detail, setDetail] = useState(a);
  useEffect(() => {
    axios.get(`${API_URL}/api/assignments/${a.id}`).then(r => setDetail(r.data)).catch(() => {});
  }, [a.id]);

  const typeInfo = TYPE_CONFIG[detail.assignment_type] || TYPE_CONFIG.devoir;

  return (
    <div className="space-y-4">
      <Card className="border-0 bg-gradient-to-r from-[#0F2C59] to-[#1a3f7a] text-white">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-white/20 text-white border-0">{typeInfo.label}</Badge>
            <Badge className="bg-white/20 text-white border-0">{detail.max_points} pts max</Badge>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2">{detail.title}</h2>
          <p className="text-blue-200 text-sm">{detail.course_name} ({detail.course_code})</p>
          <p className="text-blue-200 text-sm mt-1">Par {detail.teacher_name}</p>
          {detail.due_date && <p className="text-blue-300 text-xs mt-3 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Date limite : {new Date(detail.due_date).toLocaleString('fr-FR')}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Description</CardTitle></CardHeader>
        <CardContent><p className="text-gray-700 whitespace-pre-wrap">{detail.description}</p></CardContent>
      </Card>

      {detail.instructions && (
        <Card>
          <CardHeader><CardTitle className="text-base">Instructions</CardTitle></CardHeader>
          <CardContent><p className="text-gray-700 whitespace-pre-wrap">{detail.instructions}</p></CardContent>
        </Card>
      )}

      {/* Student: show own grade */}
      {isStudent && detail.my_submission?.status === 'graded' && (
        <Card className="border-green-200 bg-green-50/30">
          <CardContent className="p-6 text-center">
            <Award className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <p className="text-3xl font-bold text-green-700">{detail.my_submission.score} / {detail.max_points}</p>
            {detail.my_submission.feedback && <p className="text-sm text-gray-600 mt-3 bg-white rounded-lg p-3">{detail.my_submission.feedback}</p>}
          </CardContent>
        </Card>
      )}

      <div className="flex gap-3 flex-wrap">
        {isStudent && !detail.my_submission && <Button onClick={onSubmit} className="gap-2" data-testid="submit-work-button"><Send className="w-4 h-4" /> Soumettre mon travail</Button>}
        {isStudent && detail.my_submission && detail.my_submission.status !== 'graded' && <Button onClick={onSubmit} variant="outline" className="gap-2"><Pencil className="w-4 h-4" /> Modifier ma soumission</Button>}
        {isTeacher && <Button onClick={onViewSubmissions} className="gap-2" data-testid="view-submissions-btn"><ClipboardList className="w-4 h-4" /> Voir les soumissions ({detail.submission_count || 0})</Button>}
        {isTeacher && <Button variant="destructive" onClick={onDelete} className="gap-2" data-testid="delete-assignment-btn"><Trash2 className="w-4 h-4" /> Supprimer</Button>}
      </div>
    </div>
  );
};

// ============ STUDENT SUBMIT VIEW ============
const StudentSubmitView = ({ assignment, onDone }) => {
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) { toast.error('Ecrivez votre reponse'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/assignments/${assignment.id}/submit`, { content });
      toast.success('Travail soumis avec succes !');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la soumission');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="border-blue-200 bg-blue-50/30">
        <CardContent className="p-5">
          <h3 className="font-semibold text-gray-900 mb-1">{assignment.title}</h3>
          <p className="text-sm text-gray-600">{assignment.description}</p>
          {assignment.instructions && <p className="text-sm text-gray-500 mt-2 border-t pt-2">{assignment.instructions}</p>}
        </CardContent>
      </Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Votre reponse</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Redigez votre reponse ici..."
              rows={10}
              className="resize-y min-h-[200px]"
              data-testid="submission-content"
            />
          </CardContent>
        </Card>
        <div className="flex gap-3">
          <Button type="submit" disabled={saving} className="gap-2" data-testid="submit-button">
            <Send className="w-4 h-4" /> {saving ? 'Envoi en cours...' : 'Soumettre'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>Annuler</Button>
        </div>
      </form>
    </div>
  );
};

// ============ TEACHER SUBMISSIONS VIEW ============
const TeacherSubmissionsView = ({ assignment }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gradingId, setGradingId] = useState(null);
  const [gradeForm, setGradeForm] = useState({ score: '', feedback: '' });
  const [tab, setTab] = useState('submitted');

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/assignments/${assignment.id}/submissions`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [assignment.id]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleGrade = async (studentId) => {
    if (gradeForm.score === '' || isNaN(gradeForm.score)) { toast.error('Entrez une cote valide'); return; }
    const score = parseFloat(gradeForm.score);
    if (score < 0 || score > assignment.max_points) { toast.error(`La cote doit etre entre 0 et ${assignment.max_points}`); return; }
    try {
      await axios.put(`${API_URL}/api/assignments/${assignment.id}/submissions/${studentId}/grade`, {
        score, feedback: gradeForm.feedback || null
      });
      toast.success('Cote enregistree');
      setGradingId(null);
      setGradeForm({ score: '', feedback: '' });
      fetchSubmissions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <Card className="animate-pulse"><CardContent className="p-8"><div className="h-6 w-48 bg-gray-200 rounded" /></CardContent></Card>;

  const submissions = data?.submissions || [];
  const notSubmitted = data?.not_submitted || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="border-0 bg-gradient-to-r from-[#0F2C59] to-[#1a3f7a] text-white">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-1">{assignment.title}</h2>
          <p className="text-blue-200 text-sm">Correction des soumissions — {assignment.max_points} pts max</p>
          <div className="flex gap-6 mt-4 text-sm text-blue-200">
            <span>{data?.total_students || 0} etudiants</span>
            <span>{data?.submitted_count || 0} soumission(s)</span>
            <span>{data?.graded_count || 0} corrige(s)</span>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="submitted" data-testid="tab-submitted">Soumissions ({submissions.length})</TabsTrigger>
          <TabsTrigger value="missing" data-testid="tab-missing">Non soumis ({notSubmitted.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="submitted" className="mt-4 space-y-3">
          {submissions.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500">Aucune soumission</CardContent></Card>
          ) : submissions.map(sub => (
            <Card key={sub.id} className="overflow-hidden" data-testid={`submission-${sub.student_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900">{sub.student_name}</p>
                    <p className="text-xs text-gray-400 font-mono">{sub.matricule}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Soumis le {new Date(sub.submitted_at).toLocaleString('fr-FR')}</p>
                  </div>
                  {sub.status === 'graded' ? (
                    <Badge className="bg-green-50 text-green-700 border-green-200 text-sm font-bold">{sub.score}/{assignment.max_points}</Badge>
                  ) : (
                    <Badge className="bg-orange-50 text-orange-700 border-orange-200">A corriger</Badge>
                  )}
                </div>
                {/* Student's response */}
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto mb-3">
                  {sub.content}
                </div>
                {sub.feedback && (
                  <div className="bg-green-50 rounded-lg p-3 text-sm text-green-800 mb-3">
                    <p className="font-medium text-xs mb-1">Commentaire :</p>{sub.feedback}
                  </div>
                )}
                {/* Grading section */}
                {gradingId === sub.student_id ? (
                  <div className="flex items-end gap-3 mt-2 p-3 bg-blue-50 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-xs">Cote (/{assignment.max_points})</Label>
                      <Input type="number" min="0" max={assignment.max_points} step="0.5"
                        value={gradeForm.score} onChange={e => setGradeForm(p => ({ ...p, score: e.target.value }))}
                        className="h-9" data-testid="grade-score-input" />
                    </div>
                    <div className="flex-[2]">
                      <Label className="text-xs">Commentaire (optionnel)</Label>
                      <Input value={gradeForm.feedback} onChange={e => setGradeForm(p => ({ ...p, feedback: e.target.value }))}
                        placeholder="Bon travail..." className="h-9" data-testid="grade-feedback-input" />
                    </div>
                    <Button size="sm" onClick={() => handleGrade(sub.student_id)} className="h-9" data-testid="save-grade-button">
                      <CheckCircle className="w-4 h-4 mr-1" /> Valider
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setGradingId(null)} className="h-9">Annuler</Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setGradingId(sub.student_id); setGradeForm({ score: sub.score ?? '', feedback: sub.feedback ?? '' }); }}
                    data-testid={`grade-btn-${sub.student_id}`}>
                    <Pencil className="w-3.5 h-3.5" /> {sub.status === 'graded' ? 'Modifier la cote' : 'Attribuer une cote'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="missing" className="mt-4 space-y-2">
          {notSubmitted.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500">Tous les etudiants ont soumis</CardContent></Card>
          ) : notSubmitted.map(st => (
            <div key={st.student_id} className="flex items-center gap-3 p-3 bg-white rounded-lg border" data-testid={`missing-${st.student_id}`}>
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center"><AlertCircle className="w-5 h-5 text-red-400" /></div>
              <div><p className="font-medium text-gray-900 text-sm">{st.student_name}</p><p className="text-xs text-gray-400 font-mono">{st.matricule}</p></div>
              <Badge variant="outline" className="ml-auto text-red-400 border-red-200 text-xs">Non soumis</Badge>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============ CREATE ASSIGNMENT DIALOG ============
const CreateAssignmentDialog = ({ courses, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    course_id: '', title: '', description: '', instructions: '',
    assignment_type: 'devoir', max_points: '20', due_date: '', allow_late_submission: false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.course_id || !form.title || !form.description) { toast.error('Remplissez les champs obligatoires'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/assignments`, {
        ...form,
        max_points: parseFloat(form.max_points) || 20,
        due_date: form.due_date || null
      });
      toast.success('Devoir cree et publie !');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nouveau Devoir / TP</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label>Cours *</Label>
            <Select value={form.course_id} onValueChange={v => setForm(p => ({ ...p, course_id: v }))}>
              <SelectTrigger data-testid="assignment-course-select"><SelectValue placeholder="Selectionnez un cours" /></SelectTrigger>
              <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.code} - {c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Type *</Label>
            <Select value={form.assignment_type} onValueChange={v => setForm(p => ({ ...p, assignment_type: v }))}>
              <SelectTrigger data-testid="assignment-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="devoir">Devoir</SelectItem>
                <SelectItem value="tp">Travail Pratique (TP)</SelectItem>
                <SelectItem value="interrogation">Interrogation</SelectItem>
                <SelectItem value="examen">Examen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Titre *</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Ex: TP1 - Algorithmes de tri" data-testid="assignment-title-input" />
          </div>
          <div>
            <Label>Description / Enonce *</Label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Decrivez le travail a realiser..." rows={4} data-testid="assignment-desc-input" />
          </div>
          <div>
            <Label>Instructions supplementaires</Label>
            <Textarea value={form.instructions} onChange={e => setForm(p => ({ ...p, instructions: e.target.value }))} placeholder="Format de soumission, references..." rows={2} data-testid="assignment-instructions-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ponderation (points max)</Label>
              <Input type="number" min="1" max="100" value={form.max_points} onChange={e => setForm(p => ({ ...p, max_points: e.target.value }))} data-testid="assignment-points-input" />
            </div>
            <div>
              <Label>Date limite</Label>
              <Input type="datetime-local" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} data-testid="assignment-due-date" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="save-assignment-button">{saving ? 'Publication...' : 'Publier le devoir'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssignmentsPage;
